/* eslint-disable no-undef */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import connect from '../utils/connect';
import useRightsholders from '../components/hooks/inVenue/useRightsholders';
import useSeasons from '../components/hooks/inVenue/useSeasons';
import useGameDates from '../components/hooks/inVenue/useGameDates';
import useSections from '../components/hooks/inVenue/useSections';
import useAllRightsholders from '../components/hooks/useClientRighsholders';
import usePrevious from '../components/hooks/usePrevious';
import { Client, LeagueSeason } from '../types/api';
import { ClientRightsholder, Section } from '../types/inVenue';
import { useFormik, FormikValues, FormikErrors } from 'formik';
import getTruthyKeys from '../utils/getTruthyKeys';
import { shortDate } from '../utils/formatters';

type ProviderProps = {
    children: React.ReactNode;
    client: Client;
};

type FiltersProps = {
    rightsholder: ClientRightsholder | undefined;
    season: LeagueSeason | undefined;
    gameDates: string[];
    sections: Section[];
};

type ValuesProps = {
    rightsholder: number | undefined;
    season: number | undefined;
    gameDates: {
        [key: string]: boolean;
    };
    sections: {
        [key: string]: boolean;
    };
};

export const InVenueContext = React.createContext<{
    current: FiltersProps;
    isLoadingRightsholders: boolean;
    isLoadingSeasons: boolean;
    isLoadingSections: boolean;
    isLoadingGameDates: boolean;
    isLoadingAllRightsholders: boolean;
    rightsholderOptions: { value: number; name: string }[];
    seasonOptions: { value: number; name: string }[];
    sectionOptions: { value: number; label: string }[];
    gameDateOptions: { value: string; label: string }[];
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean,
    ) => Promise<void> | Promise<FormikErrors<ValuesProps>>;
    handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    values: FormikValues;
    previousValues: FormikValues;
    client: Client;
    isExpanded: boolean;
    setIsExpanded: (isExpanded: boolean) => void;
    isLoadingFilters: boolean;
    hasNoData: boolean;
    selectedDate: string;
    setSelectedDate: (selectedDate: string) => void;
}>({
    current: {
        rightsholder: undefined,
        season: undefined,
        gameDates: [],
        sections: [],
    },
    isLoadingRightsholders: true,
    isLoadingSeasons: true,
    isLoadingSections: true,
    isLoadingGameDates: true,
    isLoadingAllRightsholders: true,
    rightsholderOptions: [],
    seasonOptions: [],
    sectionOptions: [],
    gameDateOptions: [],
    setFieldValue: async () => {},
    handleSubmit: () => {},
    values: {},
    previousValues: {},
    client: undefined,
    isExpanded: false,
    setIsExpanded: () => {},
    isLoadingFilters: true,
    hasNoData: true,
    selectedDate: null,
    setSelectedDate: () => {},
});

const Provider = ({ children, client }: ProviderProps) => {
    const { id: clientId } = client;
    const initialFieldsSet = useRef([]);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [isLoadingFilters, setIsLoadingFilters] = useState<boolean>(true);
    const [hasNoData, setHasNoData] = useState<boolean>(true);
    const [selectedDate, setSelectedDate] = useState<string>(null);

    const [filters, setFilters] = useState<FiltersProps>({
        rightsholder: undefined,
        season: undefined,
        gameDates: [],
        sections: [],
    });

    let rightsholders: ClientRightsholder[] = [];
    let seasons: LeagueSeason[] = [];
    let allGameDates: string[] = [];
    let allSections: Section[] = [];

    const onSubmit = (values: ValuesProps) => {
        const rightsholder: ClientRightsholder | undefined =
            rightsholders?.find(
                rightsholder => rightsholder.id === values.rightsholder,
            );
        const season: LeagueSeason | undefined = seasons?.find(
            season => season.id === values.season,
        );
        const gameDates = getTruthyKeys(values.gameDates);
        const sections: Section[] = allSections?.filter(
            section => values.sections[section.id],
        );
        setFilters({
            rightsholder,
            season,
            gameDates,
            sections,
        });
        setIsExpanded(false);
        setSelectedDate(null);
    };

    const { values, setFieldValue, handleSubmit } = useFormik<ValuesProps>({
        initialValues: {
            rightsholder: null,
            season: null,
            gameDates: {},
            sections: {},
        },
        onSubmit,
    });

    const previousValues: ValuesProps = usePrevious(values);

    const { rightsholder: selectedRightsholderId, season: selectedSeasonId } =
        values;

    const {
        data: allRightsholders = [] as ClientRightsholder[],
        isLoading: isLoadingAllRightsholders,
    } = useAllRightsholders({
        clientId,
    });

    const { data: rightsholdersData = [], isLoading: isLoadingRightsholders } =
        useRightsholders({
            clientId,
        });

    rightsholders = rightsholdersData;

    const rightsholderOptions = rightsholders?.map(rightsholder => ({
        value: rightsholder.id,
        name: rightsholder.name,
    }));

    const selectedRightsholder = rightsholders?.find(
        rightsholder => rightsholder.id === selectedRightsholderId,
    );

    const leagueId = (allRightsholders as any[] | null)?.find(
        rightsholder => rightsholder.id === selectedRightsholderId,
    )?.leagueId;

    const { data: seasonsData = [], isLoading: isLoadingSeasons } = useSeasons({
        leagueId,
    });

    seasons = seasonsData;

    const seasonOptions = seasons?.map(season => ({
        value: season.id,
        name: `${season.year}`,
    }));

    const selectedSeason = seasons?.find(
        season => season.id === selectedSeasonId,
    );

    const { data: allGameDatesData = [], isLoading: isLoadingGameDates } =
        useGameDates({
            clientId,
            rightsholder: selectedRightsholder,
            season: selectedSeason,
        });

    allGameDates = allGameDatesData;

    const gameDateOptions = useMemo(
        () =>
            allGameDates
                ?.map(gameDate => ({
                    value: gameDate,
                    label: shortDate(gameDate),
                }))
                .sort((a, b) => {
                    const dateA = new Date(a.value);
                    const dateB = new Date(b.value);
                    return dateB.getTime() - dateA.getTime();
                }),
        [allGameDates],
    );

    const { data: allSectionsData = [], isLoading: isLoadingSections } =
        useSections({
            rightsholderId: selectedRightsholderId,
            clientId,
        });

    allSections = allSectionsData;

    const sectionOptions = allSections?.map(section => ({
        value: section.id,
        label: section.section,
    }));

    // Set Rightsholder and Season if there is only one option
    useEffect(() => {
        if (isLoadingRightsholders) {
            setIsLoadingFilters(true);
            return;
        }
        if (!isLoadingRightsholders && !rightsholders.length) {
            setIsLoadingFilters(false);
            setHasNoData(true);
        }
        if (values.rightsholder) return;
        if (!rightsholders.length) return;
        if (
            !initialFieldsSet.current.includes('rightsholder') ||
            rightsholderOptions.length === 1
        ) {
            const rightsholderId = rightsholderOptions[0]?.value;
            setFieldValue('rightsholder', rightsholderId);
            initialFieldsSet.current.push('rightsholder');
        }
    }, [isLoadingRightsholders, values.rightsholder]);

    useEffect(() => {
        if (initialFieldsSet.current.includes('rightsholder')) {
            if (isLoadingSeasons) {
                setIsLoadingFilters(true);
                return;
            }
            if (!isLoadingSeasons && !seasons.length) {
                setHasNoData(true);
            }
        }
        if (values.season) return;
        if (!seasons.length) return;
        if (
            !initialFieldsSet.current.includes('season') ||
            seasonOptions.length === 1
        ) {
            const orderedSeasons = seasonOptions.sort((a, b) => {
                const aYear = parseInt(a.name, 10);
                const bYear = parseInt(b.name, 10);
                return bYear - aYear;
            });
            const seasonId = orderedSeasons[0]?.value;
            setFieldValue('season', seasonId);
            initialFieldsSet.current.push('season');
        }
    }, [isLoadingSeasons, values.season, seasons]);

    useEffect(() => {
        if (initialFieldsSet.current.includes('seasons')) {
            if (isLoadingGameDates) {
                setIsLoadingFilters(true);
                return;
            }
            if (!isLoadingGameDates && !allGameDates.length) {
                setIsLoadingFilters(false);
                setHasNoData(true);
            }
        }
        if (getTruthyKeys(values.gameDates).length) return;
        if (!allGameDates.length) return;
        if (!initialFieldsSet.current.includes('gameDates')) {
            const selectedOptions = gameDateOptions.slice(0, 3);
            const selectedGameDates = selectedOptions.reduce(
                (acc, option) => ({
                    ...acc,
                    [option.value]: true,
                }),
                {},
            );
            setFieldValue('gameDates', selectedGameDates);
            initialFieldsSet.current.push('gameDates');
            setHasNoData(false);
        }
    }, [isLoadingGameDates, values.gameDates, allGameDates]);

    useEffect(() => {
        if (
            previousValues?.rightsholder &&
            values?.rightsholder !== previousValues?.rightsholder
        ) {
            setFieldValue('season', null);
            setFieldValue('gameDates', {});
            setFieldValue('sections', {});
        }
    }, [values?.rightsholder, previousValues?.rightsholder]);

    useEffect(() => {
        if (
            previousValues?.season &&
            values?.season !== previousValues?.season
        ) {
            setFieldValue('gameDates', {});
        }
    }, [values?.season, previousValues?.season]);

    useEffect(() => {
        const isAllInitialFieldsSet = [
            'rightsholder',
            'season',
            'gameDates',
        ].every(field => initialFieldsSet.current.includes(field));
        if (isLoadingFilters && !hasNoData && isAllInitialFieldsSet) {
            onSubmit(values);
            setIsLoadingFilters(false);
        }
    }, [isLoadingFilters, hasNoData]);

    const providerValue = {
        current: filters,
        isLoadingRightsholders,
        isLoadingSeasons,
        isLoadingSections,
        isLoadingGameDates,
        isLoadingAllRightsholders,
        rightsholderOptions,
        seasonOptions,
        sectionOptions,
        gameDateOptions,
        setFieldValue,
        handleSubmit,
        values,
        previousValues,
        client,
        isExpanded,
        setIsExpanded,
        isLoadingFilters,
        hasNoData,
        selectedDate,
        setSelectedDate,
    };

    return (
        <InVenueContext.Provider value={providerValue}>
            {children}
        </InVenueContext.Provider>
    );
};

export default connect(['client'])(Provider);
